import { Button, Stack } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { ProgressBar } from '~/@ui/progress-bar/ProgressBar'
import { SimplePageWrapper } from '~/@ui/simple-page-wrapper/SimplePageWrapper'
import { useAlert } from '~/@ui/alert/useAlert'
import { Form, useForm } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { getFormErrorMessage } from '~/@utils/form/getFormErrorMessage'
import { getErrorMessage } from '~/@utils/api/getErrorMessage'
import { getApi } from '~/@utils/api/getApi'
import { IUpdateOwnerEstateDto } from '~/@api/generated-api-types/global.types'
import { Header } from '~/@ui/header/Header'
import { FormRow } from './_components/FormRow'

const defaultValues = {
  numberOfRooms: 0,
  numberOfRestrooms: 0,
  numberOfBathrooms: 0,
  numberOfParkingSpots: 0,
  numberOfGarageSpots: 0,
}

export const PropertyDetailsPage = () => {
  const navigate = useNavigate()
  const { showAlert } = useAlert()

  const { estateId = '' } = useParams()

  const [propertyType, setPropertyType] = useState<'house' | 'flat'>()
  const isApartment = propertyType === 'flat'

  const { control, trigger, getFieldState, getValues } = useForm({
    defaultValues,
  })

  const initializeEstate = async () => {
    try {
      const { data: response } =
        await getApi().ownerEstateAppApiControllerGetOwnerEstate(estateId)

      if ('success' in response) {
        const newPropertyType = response.ownerEstate.type as 'house' | 'flat'
        setPropertyType(newPropertyType)

        return
      }

      throw new Error(
        'Nasatala neznáma chyba pri odosielaní kontaktného formulára.'
      )
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const updateEstate = async () => {
    try {
      const { data: response } =
        await getApi().ownerEstateAppApiControllerUpdateOwnerEstate(
          estateId,
          getValues() as IUpdateOwnerEstateDto
        )

      if ('success' in response) {
        navigate(`/vlastnik/dotaznik/mesacny-dochodok/${estateId}`)
        return
      }

      throw new Error(
        'Nasatala neznáma chyba pri aktualizovaní dát o nehnuteľnosti.'
      )
    } catch (err) {
      console.error(err)
      showAlert('error', getErrorMessage(err))
    }
  }

  const handleSubmit = async () => {
    const isValid = await trigger()

    if (!isValid) {
      const errorMessage = getFormErrorMessage(defaultValues, getFieldState)
      showAlert('error', errorMessage)
      return
    }

    updateEstate()
  }

  useEffect(() => {
    if (!estateId) {
      navigate('/vlastnik/dotaznik/adresa-nehnutelnosti')
    }

    initializeEstate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <SimplePageWrapper
      onClickBack={() =>
        navigate(`/vlastnik/dotaznik/informacie-o-nehnutelnosti/${estateId}`)
      }>
      <Stack className="px-5 pb-28">
        <ProgressBar
          className="mt-[26px]"
          manualPercentage={[100, 100, 100, 100]}
        />
        <Header
          title="Údaje o nehnuteľnsti"
          text={`Prosíme, konkretizujte detaily vášho ${isApartment ? 'bytu' : 'domu'}.`}
          className="mt-3"
        />

        <Form control={control} className="mt-12">
          <Stack className="gap-8">
            <FormRow
              label="Počet izieb"
              name="numberOfRooms"
              control={control}
            />
            <FormRow
              label="Počet wc"
              name="numberOfRestrooms"
              control={control}
            />
            <FormRow
              label="Počet kúpeľní"
              name="numberOfBathrooms"
              control={control}
            />
            <FormRow
              label="Počet parkovacích státí"
              name="numberOfParkingSpots"
              control={control}
            />
            <FormRow
              label="Počet garážových státí"
              name="numberOfGarageSpots"
              control={control}
            />
          </Stack>
        </Form>

        <Button
          variant="contained"
          className="w-fit h-14 self-center px-10 mt-[88px]"
          onClick={handleSubmit}>
          Pokračovať
        </Button>
      </Stack>
    </SimplePageWrapper>
  )
}
